// "People are jamming on this right now" — on the seed's own page.
//
// A jam grows out of a seed (its base) and moves on to whatever it last
// saved (its tip), so a jam is "on this seed" by either. Nothing is drawn
// when nobody is in one: the page's own Start a jam is the offer then.

import { Link } from "react-router";
import { roomPath } from "@/lib/ctas";
import { cn } from "@web-shared/lib/utils";
import { jamPath, useLiveJams, type LiveJam } from "../live";
import { Avatar } from "./Avatar";
import { participantKey } from "@shared/api-types.js";

export function useSeedLiveJams(seedId: string): LiveJam[] {
  return useLiveJams().filter((j) => j.baseSeedId === seedId || j.tipSeedId === seedId);
}

export function SeedLiveJams({ jams, compact = false, className }: { jams: LiveJam[]; compact?: boolean; className?: string }) {
  if (jams.length === 0) return null;
  return (
    <div className={cn("flex flex-col gap-2", className)}>
      {jams.map((jam) => {
        const names = jam.participants.map((p) => p.username);
        const who = names.length <= 2 ? names.join(" and ") : `${names[0]} and ${names.length - 1} others`;
        return (
          <div
            key={jam.id}
            className={cn(
              "flex items-center gap-3 rounded-xl border border-primary/30 bg-primary/[0.07]",
              compact ? "w-full px-3 py-2" : "px-3.5 py-2.5",
            )}
          >
            <span className="relative flex size-2 shrink-0" aria-hidden>
              <span className="absolute inline-flex size-full animate-ping rounded-full bg-primary opacity-60" />
              <span className="relative inline-flex size-2 rounded-full bg-primary" />
            </span>
            <span className="flex shrink-0 -space-x-1.5">
              {jam.participants.slice(0, 4).map((p) => (
                <Avatar key={participantKey(p)} name={p.username} url={null} className="size-6 ring-2 ring-background" />
              ))}
            </span>
            <p className="min-w-0 flex-1 truncate text-left text-sm">
              <span className="font-bold uppercase tracking-wider text-[10px] text-primary">Live now</span>
              <span className="ml-2 text-muted-foreground" title={names.join(", ")}>
                <span className="text-foreground">{who}</span> {names.length === 1 ? "is" : "are"} jamming on this
              </span>
            </p>
            {!compact && (
              <Link
                to={jamPath(jam.id)}
                className="shrink-0 rounded-lg px-2.5 py-1.5 text-xs font-semibold text-muted-foreground transition-colors hover:text-foreground"
              >
                Listen in
              </Link>
            )}
            <Link
              to={roomPath(jam.id)}
              className="shrink-0 rounded-lg bg-primary px-3.5 py-1.5 text-xs font-bold text-primary-foreground transition-colors hover:bg-white"
            >
              Join
            </Link>
          </div>
        );
      })}
    </div>
  );
}
